import { Link } from "@tanstack/react-router";
import { ArrowRight, KeyRound, Receipt, ShieldCheck, Video, type LucideIcon } from "lucide-react";
import { Reveal } from "./Reveal";
import { cars } from "@/lib/cars";
import { imageDims } from "@/lib/image-dims";

const deliveryPhoto = cars.find((c) => c.slug === "cadillac-escalade-2023")?.image ?? cars[0].image;

type BentoCard = {
  title: string;
  text: string;
  linkLabel: string;
  href: string;
  icon: LucideIcon;
};

const smallCards: BentoCard[] = [
  {
    title: "Filmed Before & After",
    text: "Full video walkaround at every delivery and collection.",
    linkLabel: "How it works",
    href: "/delivery",
    icon: Video,
  },
  {
    title: "Clear Pricing, No Surprises",
    text: "Every rate, deposit and fee published upfront.",
    linkLabel: "See rates",
    href: "/rates",
    icon: Receipt,
  },
  {
    title: "The Exact Car You Book",
    text: "The car in the photos is the car at your door.",
    linkLabel: "Choose a car",
    href: "/fleet",
    icon: KeyRound,
  },
  {
    title: "Discreet & Direct",
    text: "Private by default, with the owners a call away.",
    linkLabel: "Book now",
    href: "/book",
    icon: ShieldCheck,
  },
];

export function WhyAvolus() {
  return (
    <section id="why-avolus" className="bg-muted/40 border-y border-border py-16 md:py-24 lg:py-28 scroll-mt-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-12 lg:grid-cols-[minmax(0,28%)_1fr] lg:gap-16 items-start">
        <Reveal>
          <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02]">
            <span className="block text-muted-foreground">Why</span>
            <span className="block text-foreground">Avolus?</span>
          </h2>
          <p className="mt-6 text-foreground/70 leading-relaxed max-w-md">
            Avolus is a delivery-first luxury car rental run by car enthusiasts in Vancouver. Pick a car from our hand-picked fleet, get it delivered to your door, hotel or YVR arrivals, and drive off. No counter, no shuttle, no surprises.
          </p>
        </Reveal>
        <div className="grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-3">
          <Reveal className="col-span-2 md:col-span-1 md:row-span-2">
            <div className="relative h-full min-h-[420px] overflow-hidden rounded-3xl">
              <img
                src={deliveryPhoto}
                alt="Black Cadillac Escalade from the Avolus fleet, delivered in Vancouver"
                width={imageDims(deliveryPhoto).width}
                height={imageDims(deliveryPhoto).height}
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" aria-hidden="true" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <h3 className="font-display text-2xl md:text-3xl text-white">Door-to-Door Delivery</h3>
                <p className="mt-2 text-sm md:text-base text-white/70 leading-relaxed">
                  Delivered and collected on your schedule, free in Vancouver and to YVR.
                </p>
                <Link to="/delivery" className="mt-4 inline-flex min-h-11 items-center gap-2 text-sm font-medium text-white hover:underline underline-offset-4">
                  Learn more <ArrowRight size={16} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
          {smallCards.map((card) => (
            <Reveal key={card.title}>
              <div className="relative flex h-full flex-col rounded-3xl border border-border bg-card p-5 md:p-7">
                <card.icon size={22} strokeWidth={1.5} aria-hidden="true" className="absolute right-5 top-5 text-muted-foreground/60" />
                <h3 className="font-display text-lg md:text-2xl leading-snug text-foreground pr-8">{card.title}</h3>
                <p className="mt-2 text-xs md:text-sm leading-relaxed text-muted-foreground">{card.text}</p>
                <Link to={card.href} className="mt-auto inline-flex min-h-11 items-center gap-2 pt-3 text-xs md:text-sm font-medium text-foreground hover:underline underline-offset-4">
                  {card.linkLabel} <ArrowRight size={15} aria-hidden="true" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
